import { cn } from "@plasma/utils/cn";
import type { ReactNode } from "react";

import { Button } from "./ui/button";
import { Separator } from "./ui/separator";
import { SidebarTrigger } from "./ui/sidebar";

export interface PageAction {
  label: string;
  onClick: () => void;
  variant?: "default" | "outline" | "ghost" | "destructive";
  icon?: ReactNode;
  disabled?: boolean;
}

export interface PageWrapperProps {
  children: ReactNode;
  title: string;
  description?: string;
  action?: PageAction;
  actions?: PageAction[];
  /** Custom header actions rendered as raw ReactNode (e.g. export dialog, complex dropdowns) */
  headerActions?: ReactNode;
  /** Centered header content between title and actions (filters, date pickers, stats) */
  headerCenter?: ReactNode;
  logoUrl?: string | null;
  logoAlt?: string;
  /** Extra classes on the outermost container */
  className?: string;
}

export function PageWrapper({
  children,
  title,
  description,
  action,
  actions,
  headerActions,
  headerCenter,
  logoUrl,
  logoAlt,
  className,
}: PageWrapperProps) {
  const allActions = action ? [action, ...(actions ?? [])] : (actions ?? []);

  return (
    <div
      className={cn("flex h-full min-h-0 flex-col overflow-auto", className)}
    >
      <div className="sticky top-0 z-10 border-b bg-inherit">
        <div
          className={cn(
            "flex min-h-16 flex-col gap-3 px-4 py-3 sm:px-6",
            headerCenter
              ? "lg:grid lg:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] lg:items-center lg:gap-4"
              : "sm:flex-row sm:items-center sm:gap-4 sm:py-0"
          )}
        >
          <div
            className={cn(
              "flex min-w-0 items-center gap-4",
              headerCenter ? "lg:min-w-0" : "flex-1"
            )}
          >
            <SidebarTrigger />
            <Separator className="hidden h-6 sm:block" orientation="vertical" />
            {logoUrl && (
              <img
                alt={logoAlt ?? title}
                className="size-8 shrink-0 rounded-full"
                height={32}
                src={logoUrl}
                width={32}
              />
            )}
            <div className="min-w-0 flex-1">
              <h2 className="h3 truncate">{title}</h2>
              {description && (
                <p className="caption mt-0.5 truncate opacity-70">
                  {description}
                </p>
              )}
            </div>
          </div>
          {headerCenter && (
            <div className="min-w-0 lg:justify-self-center">{headerCenter}</div>
          )}
          {(allActions.length > 0 || headerActions) && (
            <div
              className={cn(
                "flex flex-wrap items-center gap-2",
                headerCenter
                  ? "lg:justify-end lg:justify-self-end"
                  : "sm:flex-nowrap"
              )}
            >
              {headerActions}
              {allActions.map((pageAction) => (
                <Button
                  className="flex-1 sm:flex-none"
                  disabled={pageAction.disabled}
                  key={pageAction.label}
                  onClick={pageAction.onClick}
                  size="sm"
                  variant={pageAction.variant ?? "default"}
                >
                  {pageAction.icon && (
                    <span className="mr-2 size-4">{pageAction.icon}</span>
                  )}
                  <span className="truncate">{pageAction.label}</span>
                </Button>
              ))}
            </div>
          )}
        </div>
      </div>
      <div className="min-h-0 flex-1 p-4 sm:p-6">{children}</div>
    </div>
  );
}
